import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, CircularProgress, Alert, Divider, Grid, Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageCard } from '../components/common/PageCard';
import { PageHint } from '../components/common/PageHint';
import { toastService } from '../services/toast.service';
import api from '../services/api';

interface EmbeddingStats { indexedCount: number }
interface RebuildResult { built: number; skipped: number; failed?: number }
interface TestResult { success: boolean; log: string[] }

interface IndexBlock {
  label: string;
  description: string;
  statsUrl: string;
  rebuildUrl: string;
  testUrl?: string;
}

const BLOCKS: IndexBlock[] = [
  {
    label: 'Продукти харчування',
    description: 'nest_foods (dataset=1). Запускай після імпорту нових продуктів або зміни назв.',
    statsUrl: '/admin/ai-cache/foods/embedding-stats',
    rebuildUrl: '/admin/ai-cache/foods/rebuild-embeddings',
    testUrl: '/admin/ai-cache/foods/test-embedding',
  },
  {
    label: 'Тренування (МЕТ-база)',
    description: 'nest_workouts_v2 (dataset=1). Запускай після оновлення офіційної МЕТ-бази.',
    statsUrl: '/admin/ai-cache/workouts/embedding-stats',
    rebuildUrl: '/admin/ai-cache/workouts/rebuild-embeddings',
    testUrl: '/admin/ai-cache/workouts/test-embedding',
  },
  {
    label: 'База MET (Compendium, adult)',
    description: 'nest_met_activities: назва + переклади + ключові фрази. Кандидати, з яких AI вибирає MET для калорій. Оновлює лише нові й змінені записи.',
    statsUrl: '/admin/ai-cache/workouts/met-embedding-stats',
    rebuildUrl: '/admin/ai-cache/workouts/rebuild-met-embeddings',
  },
];

function IndexBlock({ block }: { block: IndexBlock }) {
  const [stats, setStats] = useState<EmbeddingStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(block.statsUrl);
      setStats(res.data.data);
    } catch {
      toastService.error('Не вдалося завантажити статистику');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const handleRebuild = async () => {
    setRebuilding(true);
    setRebuildResult(null);
    try {
      const res = await api.post(block.rebuildUrl);
      // Фонова перебудова (база MET): відповідь - стан; чекаємо завершення, опитуючи статистику.
      let result = res.data.data?.build ?? null;
      while (result?.running) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        result = (await api.get(block.statsUrl)).data.data?.build ?? null;
      }
      const built = result ? result.built : res.data.built;
      const skipped = result ? result.unchanged : res.data.skipped;
      const failed = result ? result.failed : res.data.failed;
      setRebuildResult({ built, skipped, failed });
      if (failed || result?.error) toastService.error(`${block.label}: ${result?.error ?? `помилка для ${failed} записів`}`);
      else toastService.success(`${block.label}: переіндексовано ${built} записів`);
      await loadStats();
    } catch (error: any) {
      toastService.error(error?.message || 'Помилка переіндексації');
    } finally {
      setRebuilding(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(block.testUrl!);
      setTestResult({ success: res.data.success, log: res.data.log ?? [] });
      if (res.data.success) {
        toastService.success('Тест пройшов успішно');
        await loadStats();
      } else {
        toastService.error('Тест завершився з помилкою — дивись лог');
      }
    } catch (error: any) {
      setTestResult({ success: false, log: [`HTTP помилка: ${error?.message}`] });
      toastService.error(error?.message || 'Помилка тесту');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {block.label}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <StorageIcon color="action" fontSize="small" />
        {statsLoading ? (
          <CircularProgress size={18} />
        ) : stats ? (
          <Typography variant="body2">
            Проіндексовано: <strong>{stats.indexedCount}</strong> записів
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}
        <Button size="small" onClick={loadStats} disabled={statsLoading}>
          Оновити
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {block.description} Повна індексація займає ~2–5 хвилин.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {block.testUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <BugReportOutlinedIcon />}
            onClick={handleTest}
            disabled={testing || rebuilding}
          >
            {testing ? 'Тестування…' : 'Тест (1 запис)'}
          </Button>
        )}

        <Button
          variant="contained"
          size="small"
          startIcon={rebuilding ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={handleRebuild}
          disabled={rebuilding || testing}
        >
          {rebuilding ? 'Переіндексація…' : 'Переіндексувати всі'}
        </Button>
      </Box>

      {testResult && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 1 }}>
            {testResult.success ? 'Тест пройшов успішно' : 'Тест завершився з помилкою'}
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
            {testResult.log.map((line, i) => (
              <Typography
                key={i}
                variant="caption"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  color: line.startsWith('ПОМИЛКА') ? 'error.main' : 'text.primary',
                  lineHeight: 1.8,
                }}
              >
                {line}
              </Typography>
            ))}
          </Paper>
        </Box>
      )}

      {rebuildResult && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Готово: <strong>{rebuildResult.built}</strong> проіндексовано,{' '}
          <strong>{rebuildResult.skipped}</strong> пропущено
          {rebuildResult.failed ? <>, <strong>{rebuildResult.failed}</strong> з помилкою</> : null}.
        </Alert>
      )}
    </Box>
  );
}

const DevAiIndexPageContent: React.FC = () => (
  <Box>
    <PageHint iDoc={PageIndices.DEV_AI_INDEX} />

    <PageCard>
      <Grid container spacing={4}>
        {BLOCKS.map((block, i) => (
          <React.Fragment key={block.rebuildUrl}>
            {i > 0 && (
              <Grid item xs={12}>
                <Divider />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <IndexBlock block={block} />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </PageCard>
  </Box>
);

export const DevAiIndexPage: React.FC = () => (
  <DashboardLayout data-i-doc={PageIndices.DEV_AI_INDEX}>
    <DevAiIndexPageContent />
  </DashboardLayout>
);
